/**
 * DockDropPreview — the overlay shown on a panel while another panel is being
 * dragged onto it (design option A: insertion caret). The target grows a
 * preview tab strip at its top — its own tab (solid) plus the incoming panel's
 * tab (dashed ghost) — and a blue caret marks the slot the incoming tab will
 * drop into. The side of the panel the pointer is over (panelDock.candidateSide)
 * picks that slot, so you choose left-vs-right tab order before releasing.
 *
 * Rendered by BOTH standalone panels IN PLACE OF their own title header while
 * they are the drop candidate (so the panel's own "Add"/"Format" label doesn't
 * show twice — once here as a tab, once in the header). Self-gates via the
 * store: returns null unless it is the current drop candidate. In-flow +
 * pointer-transparent (the drag is driven by the other panel).
 *
 * react-strict-dom (`html.*` + `css.create`) — compiles to DOM on web, ports to
 * native.
 */
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { usePanelDock, TAB_LABEL, type DockTab } from "./panelDock.js";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";

const s = css.create({
  strip: {
    position: "relative", // caret is absolute within
    pointerEvents: "none",
    display: "flex",
    flexDirection: "row",
    gap: microSpacing.px2,
    paddingTop: microSpacing.px5,
    paddingRight: microSpacing.px6,
    paddingLeft: microSpacing.px6,
    backgroundColor: colors.bgCard,
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  tab: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: sizes.chip,
    paddingLeft: spacing.ms,
    paddingRight: spacing.ms,
    fontSize: "0.8rem",
    fontWeight: typography.fontWeightSemibold,
    borderTopLeftRadius: radii.px6,
    borderTopRightRadius: radii.px6,
    marginBottom: -1,
    whiteSpace: "nowrap",
  },
  ghost: {
    color: colors.accent,
    backgroundColor: colors.infoBg,
    borderWidth: borders.hairline,
    borderStyle: "dashed",
    borderColor: colors.accent,
  },
  solid: {
    color: colors.text,
    backgroundColor: colors.bg,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderBottomColor: colors.bg,
  },
  caret: {
    position: "absolute",
    top: 4,
    bottom: 0,
    width: sizes.navMarkerW,
    borderRadius: radii.xs,
    backgroundColor: colors.accent,
    boxShadow: shadows.dropOver,
  },
  caretLeft: { left: 5 },
  caretRight: { right: 5 },
});

export function DockDropPreview({ me }: { me: DockTab }) {
  const { candidate, candidateSide, dragSource } = usePanelDock();
  if (candidate !== me || !dragSource) return null;

  const incoming: DockTab = dragSource;
  const side = candidateSide ?? "right";
  // Incoming tab sits on the hovered side of the target's own tab.
  const order: DockTab[] = side === "left" ? [incoming, me] : [me, incoming];

  return (
    <html.div aria-hidden style={s.strip}>
      {order.map((t) => (
        <html.span key={t} style={[s.tab, t === incoming ? s.ghost : s.solid]}>
          {TAB_LABEL[t]}
        </html.span>
      ))}
      {/* Insertion caret at the chosen edge. */}
      <html.span
        style={[s.caret, side === "left" ? s.caretLeft : s.caretRight]}
      />
    </html.div>
  );
}
